import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';

export interface UserListItem {
  id: string;
  telegramId: string;
  username: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber: string;
  languageCode: string;
  isPremium: boolean;
  isActive: boolean;
  balance: number;
  referredBy: string | null;
  createdAt: string;
}

export interface UserDetail extends UserListItem {}

export interface UserStats {
  gamesPlayed: number;
  totalWins: number;
  totalPrize: number;
  totalCreditTransactions: number;
  totalCredited: number;
  totalWithdrawals: number;
  totalWithdrawn: number;
  referredCount: number;
  totalReferralRevenue: number;
  invitedByThisUser: number;
}

export interface UserDetailResponse {
  user: UserDetail;
  stats: UserStats;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface UsersResponse {
  users: UserListItem[];
  pagination: Pagination;
}

/* =========================================================
   USER GAMES — mirrors the game-history list shape
========================================================= */

export interface UserGameListItem {
  id: string;
  historyId: string;
  roomId: string;
  betAmount: number;
  commissionPercent: number;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  playersCount: number;
  totalCartelasSold: number;
  prizePool: number;
  netPrizePool: number;
  platformRevenue: number;
  winnersCount: number;
  callIndex: number;
  winnerCallIndex: number | null;
  createdAt: string;
}

export interface UserGamesResponse {
  games: UserGameListItem[];
  pagination: Pagination;
}

export type UserGamesMode = 'played' | 'won';

export interface GetUserGamesParams {
  page?: number;
  limit?: number;
  search?: string;
  minDate?: string;
  maxDate?: string;
  mode?: UserGamesMode;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | 'active' | 'disabled';
}

class UserService {
  async getAll(params: GetUsersParams = {}): Promise<UsersResponse> {
    const res = await apiClient.get<ApiEnvelope<UsersResponse>>(
      API_ENDPOINTS.USERS.GET_ALL,
      { params }
    );
    return res.data.data;
  }

  async getOne(id: string): Promise<UserDetailResponse> {
    const res = await apiClient.get<ApiEnvelope<UserDetailResponse>>(
      API_ENDPOINTS.USERS.GET_ONE(id)
    );
    return res.data.data;
  }

  async toggleActive(id: string): Promise<{ id: string; isActive: boolean }> {
    const res = await apiClient.patch<
      ApiEnvelope<{ id: string; isActive: boolean }>
    >(API_ENDPOINTS.USERS.TOGGLE_ACTIVE(id));
    return res.data.data;
  }

  async updateBalance(
    id: string,
    balance: number
  ): Promise<{ id: string; balance: number }> {
    const res = await apiClient.patch<
      ApiEnvelope<{ id: string; balance: number }>
    >(API_ENDPOINTS.USERS.UPDATE_BALANCE(id), { balance });
    return res.data.data;
  }

  async getUserGames(
    id: string,
    params: GetUserGamesParams = {}
  ): Promise<UserGamesResponse> {
    const res = await apiClient.get<ApiEnvelope<UserGamesResponse>>(
      API_ENDPOINTS.USERS.GET_GAMES(id),
      { params }
    );
    return res.data.data;
  }
}

const userService = new UserService();
export default userService;